import axios from 'axios'

export default class TaxAPI {
  constructor(token) {
    this.token = token
    this.baseUrl = window.config.API_URL
  }

  // ============================================================
  // TAX PERIODS
  // ============================================================

  /**
   * Get tax dashboard summary
   * @returns {Promise} Dashboard data with current period, upcoming, overdue, YTD
   */
  async getDashboard() {
    return await axios.get(`${this.baseUrl}TaxPeriods/dashboard`, {
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  /**
   * Get paginated list of tax periods
   * @param {Object} params - Query parameters (year, status, page, pageSize, etc.)
   * @returns {Promise} Paginated list of tax periods
   */
  async getPeriods(params = {}) {
    return await axios.get(`${this.baseUrl}TaxPeriods/currentBusiness`, {
      params,
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  /**
   * Get detailed tax period by ID
   * @param {String} id - Tax period ID
   * @returns {Promise} Tax period with transaction breakdown
   */
  async getPeriod(id) {
    return await axios.get(`${this.baseUrl}TaxPeriods/${id}`, {
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  /**
   * Create a new tax period manually
   * @param {Number} year - Year (e.g., 2026)
   * @param {Number} month - Month (1-12)
   * @returns {Promise} Created tax period
   */
  async createPeriod(year, month) {
    return await axios.post(`${this.baseUrl}TaxPeriods/create/${year}/${month}`, {}, {
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  /**
   * Recalculate tax period totals from transactions
   * @param {String} id - Tax period ID
   * @returns {Promise} Updated tax period
   */
  async recalculatePeriod(id) {
    return await axios.put(`${this.baseUrl}TaxPeriods/${id}/recalculate`, {}, {
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  /**
   * Update tax period status
   * @param {String} id - Tax period ID
   * @param {Number} status - 0=Open, 1=Closed, 2=Filed
   * @returns {Promise} Updated tax period
   */
  async updatePeriodStatus(id, status) {
    return await axios.put(`${this.baseUrl}TaxPeriods/${id}/status`,
      { status },
      { headers: { Authorization: `Bearer ${this.token}` }}
    )
  }

  // ============================================================
  // TAX TRANSACTIONS
  // ============================================================

  /**
   * Get paginated list of tax transactions
   * @param {Object} params - Query parameters (taxPeriodId, transactionType, sourceType, status, date range, etc.)
   * @returns {Promise} Paginated list of tax transactions
   */
  async getTransactions(params = {}) {
    return await axios.get(`${this.baseUrl}TaxTransactions/currentBusiness`, {
      params,
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  /**
   * Get detailed tax transaction by ID
   * @param {String} id - Tax transaction ID
   * @returns {Promise} Tax transaction details
   */
  async getTransaction(id) {
    return await axios.get(`${this.baseUrl}TaxTransactions/${id}`, {
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  /**
   * Get all transactions for a specific tax period
   * @param {String} periodId - Tax period ID
   * @returns {Promise} List of transactions
   */
  async getTransactionsByPeriod(periodId) {
    return await axios.get(`${this.baseUrl}TaxTransactions/period/${periodId}`, {
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  /**
   * Get tax summary for a date range
   * @param {String} startDate - Start date (YYYY-MM-DD)
   * @param {String} endDate - End date (YYYY-MM-DD)
   * @returns {Promise} Tax summary with output/input totals
   */
  async getTransactionSummary(startDate, endDate) {
    return await axios.get(`${this.baseUrl}TaxTransactions/summary`, {
      params: { startDate, endDate },
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  /**
   * Update tax transaction status
   * @param {String} id - Tax transaction ID
   * @param {Number} status - 0=Pending, 1=Approved, 2=Rejected
   * @returns {Promise} Updated transaction
   */
  async updateTransactionStatus(id, status) {
    return await axios.put(`${this.baseUrl}TaxTransactions/${id}/status`,
      { status },
      { headers: { Authorization: `Bearer ${this.token}` }}
    )
  }

  // ============================================================
  // TAX REPORTS
  // ============================================================

  /**
   * Get Malaysian SST-03 return form data
   * @param {String} periodId - Tax period ID
   * @returns {Promise} SST-03 form data (JSON)
   */
  async getSST03Report(periodId) {
    return await axios.get(`${this.baseUrl}TaxReports/sst03/${periodId}`, {
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  /**
   * Get monthly tax summary with daily breakdown
   * @param {Number} year - Year (e.g., 2026)
   * @param {Number} month - Month (1-12)
   * @returns {Promise} Monthly summary
   */
  async getMonthlySummary(year, month) {
    return await axios.get(`${this.baseUrl}TaxReports/monthly-summary/${year}/${month}`, {
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  /**
   * Get annual tax summary with monthly breakdown
   * @param {Number} year - Year (e.g., 2026)
   * @returns {Promise} Annual summary
   */
  async getAnnualSummary(year) {
    return await axios.get(`${this.baseUrl}TaxReports/annual-summary/${year}`, {
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  /**
   * Get detailed transaction listing for a period
   * @param {String} periodId - Tax period ID
   * @returns {Promise} Detailed transaction listing
   */
  async getTransactionListing(periodId) {
    return await axios.get(`${this.baseUrl}TaxReports/transaction-listing/${periodId}`, {
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  // ============================================================
  // TAX PAYMENTS
  // ============================================================

  /**
   * Get all payments for a specific tax period
   * @param {String} periodId - Tax period ID
   * @returns {Promise} List of payments
   */
  async getPaymentsByPeriod(periodId) {
    return await axios.get(`${this.baseUrl}TaxPayments/period/${periodId}`, {
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  /**
   * Create a new tax payment
   * @param {Object} paymentData - Payment details
   * @param {String} paymentData.taxPeriodId - Tax period ID
   * @param {String} paymentData.paymentDate - Payment date (ISO format)
   * @param {Number} paymentData.taxAmount - Amount paid for tax
   * @param {Number} paymentData.penaltyAmount - Amount paid for penalty
   * @param {String} paymentData.paymentMethod - Payment method (e.g., "FPX", "Bank Transfer")
   * @param {String} paymentData.paymentReference - Payment reference number
   * @param {String} paymentData.notes - Additional notes
   * @param {String} paymentData.documentId - Document ID for receipt (optional)
   * @param {String} paymentData.bankAccountId - Bank account ID (optional)
   * @returns {Promise} Created payment
   */
  async createPayment(paymentData) {
    return await axios.post(`${this.baseUrl}TaxPayments`, paymentData, {
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  /**
   * Cancel a tax payment
   * @param {String} id - Payment ID
   * @returns {Promise} Empty response
   */
  async cancelPayment(id) {
    return await axios.delete(`${this.baseUrl}TaxPayments/${id}`, {
      headers: { Authorization: `Bearer ${this.token}` }
    })
  }

  // ============================================================
  // HELPER METHODS
  // ============================================================

  /**
   * Get tax period status label
   * @param {Number} status - Status code (0, 1, 2)
   * @returns {String} Status label
   */
  getStatusLabel(status) {
    const labels = {
      0: 'Open',
      1: 'Closed',
      2: 'Filed'
    }
    return labels[status] || 'Unknown'
  }

  /**
   * Get tax period status badge color
   * @param {Number} status - Status code (0, 1, 2)
   * @returns {String} CoreUI badge color
   */
  getStatusColor(status) {
    const colors = {
      0: 'success',  // Open - green
      1: 'warning',  // Closed - yellow
      2: 'info'      // Filed - blue
    }
    return colors[status] || 'secondary'
  }

  /**
   * Get transaction type label
   * @param {Number} type - Transaction type (0=Output, 1=Input)
   * @returns {String} Type label
   */
  getTransactionTypeLabel(type) {
    return type === 0 ? 'Output Tax (Sales)' : 'Input Tax (Purchases)'
  }

  /**
   * Get transaction type badge color
   * @param {Number} type - Transaction type (0=Output, 1=Input)
   * @returns {String} CoreUI badge color
   */
  getTransactionTypeColor(type) {
    return type === 0 ? 'danger' : 'success'
  }

  /**
   * Format currency (Malaysian Ringgit)
   * @param {Number} amount - Amount to format
   * @returns {String} Formatted amount (e.g., "RM 1,234.56")
   */
  formatCurrency(amount) {
    return 'RM ' + parseFloat(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
  }

  /**
   * Format date to Malaysian format
   * @param {String|Date} date - Date to format
   * @returns {String} Formatted date (e.g., "15/02/2026")
   */
  formatDate(date) {
    if (!date) return '-'
    const d = new Date(date)
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = d.getFullYear()
    return `${day}/${month}/${year}`
  }

  /**
   * Check if period is overdue
   * @param {Object} period - Tax period object
   * @returns {Boolean} True if overdue
   */
  isOverdue(period) {
    if (period.status !== 0) return false // Only open periods can be overdue
    return new Date(period.dueDate) < new Date()
  }

  /**
   * Get days until due date
   * @param {String|Date} dueDate - Due date
   * @returns {Number} Days until due (negative if overdue)
   */
  getDaysUntilDue(dueDate) {
    const now = new Date()
    const due = new Date(dueDate)
    const diffTime = due - now
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  /**
   * Get urgency badge for period
   * @param {Object} period - Tax period object
   * @returns {Object} Badge with color and text
   */
  getUrgencyBadge(period) {
    if (period.status !== 0) {
      return { color: 'secondary', text: this.getStatusLabel(period.status) }
    }

    const days = this.getDaysUntilDue(period.dueDate)

    if (days < 0) {
      return { color: 'danger', text: `OVERDUE (${Math.abs(days)} days)` }
    } else if (days <= 7) {
      return { color: 'warning', text: `DUE IN ${days} DAYS` }
    } else if (days <= 30) {
      return { color: 'info', text: `Due in ${days} days` }
    } else {
      return { color: 'success', text: 'On Track' }
    }
  }
}